import { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, doc, updateDoc, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { Receipt, Clock, CheckCircle2, XCircle, Package, X, LayoutGrid, List } from 'lucide-react';

interface Transaction {
  id: string;
  userId: string;
  customerEmail?: string;
  items: any[];
  totalAmount: number;
  provider: string;
  status: string;
  fulfillmentStatus?: string;
  pickupTime?: string;
  pickupTimes?: Record<string, string>;
  createdAt: any;
}

export function TransactionHistory({ vendorId }: { vendorId?: string }) {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'board'>('list');

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      let q;
      if (vendorId) {
        // If vendor, only show transactions that contain their items
        q = query(
          collection(db, 'transactions'), 
          where('vendorIds', 'array-contains', vendorId)
        );
      } else {
        q = query(collection(db, 'transactions'), orderBy('createdAt', 'desc'));
      }
      
      const querySnapshot = await getDocs(q);
      let fetchedTransactions = querySnapshot.docs.map(doc => {
        const data = doc.data() as any;
        
        let items = data.items || [];
        let totalAmount = data.totalAmount || 0;

        if (vendorId) {
          items = items.filter((item: any) => item.vendorId === vendorId);
          totalAmount = items.reduce((sum: number, item: any) => sum + (item.price * item.quantity), 0);
        }

        return {
          id: doc.id,
          userId: data.userId,
          items: items,
          totalAmount: totalAmount,
          status: data.status,
          provider: data.provider,
          customerEmail: data.customerEmail,
          fulfillmentStatus: data.fulfillmentStatus,
          pickupTime: data.pickupTime,
          pickupTimes: data.pickupTimes,
          createdAt: data.createdAt
        } as Transaction;
      });

      if (vendorId) {
        // Sort client-side to avoid needing a composite index
        fetchedTransactions.sort((a, b) => {
          const timeA = a.createdAt?.toMillis() || 0;
          const timeB = b.createdAt?.toMillis() || 0;
          return timeB - timeA;
        });
      }

      setTransactions(fetchedTransactions);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [vendorId]);

  const updateFulfillmentStatus = async (transactionId: string, newStatus: string) => {
    try {
      await updateDoc(doc(db, 'transactions', transactionId), {
        fulfillmentStatus: newStatus
      });
      setTransactions(transactions.map(tx => 
        tx.id === transactionId ? { ...tx, fulfillmentStatus: newStatus } : tx
      ));
    } catch (error) {
      console.error("Error updating fulfillment status:", error);
      alert("Failed to update status.");
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-amber-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/50">{getStatusIcon(status)} Completed</span>;
      case 'failed':
        return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-100 dark:border-red-900/50">{getStatusIcon(status)} Failed</span>;
      default:
        return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-100 dark:border-amber-900/50">{getStatusIcon(status)} Pending</span>;
    }
  };

  const PLATFORM_FEE_PERCENT = 0.035;

  const totalEarnings = transactions
    .filter(tx => tx.status === 'completed')
    .reduce((sum, tx) => sum + tx.totalAmount, 0);

  const netEarnings = totalEarnings * (1 - PLATFORM_FEE_PERCENT);

  return (
    <div className="space-y-6">
      {vendorId && !loading && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-neutral-900 p-6 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-sm">
            <h3 className="text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">Total Earnings (Net)</h3>
            <p className="text-3xl font-serif text-neutral-900 dark:text-white">${(netEarnings / 100).toFixed(2)}</p>
            <p className="text-[10px] text-neutral-400 mt-1 italic">5% platform fee applied</p>
          </div>
          <div className="bg-white dark:bg-neutral-900 p-6 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-sm">
            <h3 className="text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">Completed Orders</h3>
            <p className="text-3xl font-serif text-neutral-900 dark:text-white">{transactions.filter(tx => tx.status === 'completed').length}</p>
          </div>
          <div className="bg-white dark:bg-neutral-900 p-6 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-sm">
            <h3 className="text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-1">Pending Orders</h3>
            <p className="text-3xl font-serif text-neutral-900 dark:text-white">{transactions.filter(tx => tx.status === 'pending').length}</p>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm">
        <div className="p-8 border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-medium text-neutral-900 dark:text-white flex items-center gap-2 font-serif">
              <Receipt className="w-5 h-5 text-neutral-500 dark:text-neutral-400 stroke-[1.5]" />
              Transaction History
            </h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2 font-light">View all customer purchases and payment statuses.</p>
          </div>
          {vendorId && (
            <div className="flex bg-neutral-200 dark:bg-neutral-800 p-1 rounded-lg">
               <button
                 onClick={() => setViewMode('list')}
                 className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white shadow-sm' : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'}`}
               >
                 <List className="w-4 h-4" /> List
               </button>
               <button
                 onClick={() => setViewMode('board')}
                 className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'board' ? 'bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white shadow-sm' : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'}`}
               >
                 <LayoutGrid className="w-4 h-4" /> Board
               </button>
            </div>
          )}
        </div>
        
        {loading ? (
        <div className="p-8 text-center text-neutral-500 dark:text-neutral-400">Loading transactions...</div>
      ) : transactions.length === 0 ? (
        <div className="text-center py-24 bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-100 dark:border-neutral-800">
           <Package className="mx-auto h-12 w-12 text-neutral-300 dark:text-neutral-600 mb-4 stroke-[1.5]" />
           <h3 className="text-lg font-medium text-neutral-900 dark:text-white">No transactions yet</h3>
           <p className="mt-1 text-neutral-500 dark:text-neutral-400">When customers place orders, they will appear here.</p>
        </div>
      ) : viewMode === 'board' ? (
         <div className="p-8 overflow-x-auto bg-neutral-100/50 dark:bg-neutral-900/20">
            <div className="flex gap-6 min-w-max pb-4">
               {['pending', 'preparing', 'ready_for_pickup', 'completed'].map(status => {
                  const statusTx = transactions.filter(t => (t.status === 'completed') && (t.fulfillmentStatus || 'pending') === status);
                  return (
                    <div key={status} className="w-80 bg-neutral-50 dark:bg-neutral-900/50 rounded-2xl p-4 border border-neutral-200 dark:border-neutral-800 flex flex-col h-[650px] shadow-sm">
                       <h3 className="font-medium text-neutral-900 dark:text-white capitalize mb-4 flex items-center justify-between">
                          <span className="flex items-center gap-2">
                             {status === 'pending' && <span className="w-2 h-2 rounded-full bg-neutral-400" />}
                             {status === 'preparing' && <span className="w-2 h-2 rounded-full bg-amber-400" />}
                             {status === 'ready_for_pickup' && <span className="w-2 h-2 rounded-full bg-blue-400" />}
                             {status === 'completed' && <span className="w-2 h-2 rounded-full bg-emerald-400" />}
                             {status.replace(/_/g, ' ')}
                          </span>
                          <span className="text-xs bg-neutral-200 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 px-2 py-0.5 rounded-full">
                             {statusTx.length}
                          </span>
                       </h3>
                       <div className="flex-1 overflow-y-auto space-y-3 pr-2 custom-scrollbar">
                          {statusTx.map(tx => (
                             <div key={tx.id} className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                                <div className="flex justify-between items-start mb-2">
                                   <span className="font-mono text-xs text-neutral-500 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 px-2 py-0.5 rounded">#{tx.id?.slice(0, 6)}</span>
                                   <span className="text-xs text-neutral-400">{tx.createdAt?.toDate ? tx.createdAt.toDate().toLocaleDateString() : 'Unknown'}</span>
                                </div>
                                <h4 className="font-medium text-sm text-neutral-900 dark:text-white mb-1 truncate">{tx.customerEmail || 'Unknown Customer'}</h4>
                                
                                <div className="space-y-1 mb-3 pt-2 border-t border-neutral-100 dark:border-neutral-800">
                                   {tx.items.slice(0, 2).map((item, idx) => (
                                      <div key={idx} className="text-xs text-neutral-600 dark:text-neutral-400 flex items-start gap-1">
                                         <span className="font-medium text-neutral-900 dark:text-white">{item.quantity}x</span>
                                         <span className="truncate">{item.name}</span>
                                      </div>
                                   ))}
                                   {tx.items.length > 2 && (
                                     <div className="text-xs text-neutral-400 italic">+{tx.items.length - 2} more items</div>
                                   )}
                                </div>
                                
                                <div className="flex items-center justify-between pt-3 border-t border-neutral-100 dark:border-neutral-800">
                                   <span className="font-medium text-sm text-neutral-900 dark:text-white">${(tx.totalAmount / 100).toFixed(2)}</span>
                                   
                                   <select
                                      value={tx.fulfillmentStatus || 'pending'}
                                      onChange={(e) => updateFulfillmentStatus(tx.id, e.target.value)}
                                      className="text-xs font-medium rounded-md px-2 py-1 border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 focus:outline-none focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white transition-colors cursor-pointer w-28"
                                   >
                                      <option value="pending">Pending</option>
                                      <option value="preparing">Preparing</option>
                                      <option value="ready_for_pickup">Ready</option>
                                      <option value="completed">Completed</option>
                                   </select>
                                </div>
                             </div>
                          ))}
                          {statusTx.length === 0 && (
                             <div className="h-full flex flex-col items-center justify-center text-neutral-400 dark:text-neutral-600 p-6 text-center border-2 border-dashed border-neutral-200 dark:border-neutral-800 rounded-xl bg-neutral-50/50 dark:bg-neutral-900/20">
                                <Package className="w-8 h-8 mb-2 stroke-[1.5] opacity-50" />
                                <span className="text-xs">No orders</span>
                             </div>
                          )}
                       </div>
                    </div>
                  );
               })}
            </div>
         </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-neutral-50 dark:bg-neutral-800/50 border-b border-neutral-100 dark:border-neutral-800 text-neutral-500 dark:text-neutral-400">
              <tr>
                <th className="px-6 py-4 font-medium">Transaction ID</th>
                <th className="px-6 py-4 font-medium">Date</th>
                <th className="px-6 py-4 font-medium">Customer</th>
                <th className="px-6 py-4 font-medium">Items</th>
                <th className="px-6 py-4 font-medium">Amount</th>
                <th className="px-6 py-4 font-medium">Payment</th>
                <th className="px-6 py-4 font-medium text-right">Fulfillment</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {transactions.map((tx) => (
                <tr key={tx.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors">
                  <td className="px-6 py-4 font-mono text-xs text-neutral-500 dark:text-neutral-400">{tx.id?.slice(0, 8)}...</td>
                  <td className="px-6 py-4 text-neutral-900 dark:text-white">
                    {tx.createdAt?.toDate ? tx.createdAt.toDate().toLocaleDateString() : 'Unknown'}
                  </td>
                  <td className="px-6 py-4 text-neutral-900 dark:text-white">
                    <div className="text-sm flex items-center gap-2">
                      {tx.customerEmail || 'Unknown Email'}
                      {tx.userId && (
                        <button
                          onClick={() => {
                            if (!doc || !tx.userId) return;
                            // Emit a custom event or you can handle creating a thread here
                            window.dispatchEvent(new CustomEvent('start-support-chat', { 
                              detail: { targetId: tx.userId, targetName: tx.customerEmail || 'Customer', role: 'customer' } 
                            }));
                          }}
                          className="text-indigo-500 hover:text-indigo-700"
                          title="Message Customer"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
                        </button>
                      )}
                    </div>
                    <div className="font-mono text-[10px] text-neutral-400 dark:text-neutral-500 truncate max-w-[120px]" title={tx.userId}>
                      {tx.userId}
                    </div>
                    {tx.pickupTime && (
                      <div className="mt-1 text-xs text-neutral-600 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-800 px-2 py-1 rounded inline-block">
                        <span className="font-medium">Pickup:</span> {tx.pickupTime}
                      </div>
                    )}
                    {tx.pickupTimes && Object.entries(tx.pickupTimes).map(([vId, time]) => {
                      if (vendorId && vId !== vendorId) return null;
                      return (
                        <div key={vId} className="mt-1 text-xs text-neutral-600 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-800 px-2 py-1 rounded inline-block">
                          <span className="font-medium">Pickup:</span> {time}
                        </div>
                      );
                    })}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      {tx.items.map((item, idx) => (
                        <div key={idx} className="text-xs text-neutral-600 dark:text-neutral-400 flex items-center gap-1">
                          <span>{item.quantity}x</span>
                          <button 
                            onClick={() => setSelectedItem(item)}
                            className="font-medium text-neutral-900 dark:text-white hover:underline text-left"
                          >
                            {item.name || 'Unknown Item'}
                          </button>
                          {item.vendorName && <span className="text-neutral-400 dark:text-neutral-500"> ({item.vendorName})</span>}
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-neutral-900 dark:text-white font-medium">
                    ${(tx.totalAmount / 100).toFixed(2)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1 items-start">
                      <span className="text-xs text-neutral-500 dark:text-neutral-400 capitalize">{tx.provider}</span>
                      {getStatusBadge(tx.status)}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    {tx.status === 'completed' ? (
                      <div className="flex justify-end gap-1">
                        <select
                          value={tx.fulfillmentStatus || 'pending'}
                          onChange={(e) => updateFulfillmentStatus(tx.id, e.target.value)}
                          className={`text-xs font-medium rounded-md px-3 py-1.5 border border-transparent cursor-pointer focus:ring-2 focus:ring-neutral-900 dark:focus:ring-white transition-colors ${
                            tx.fulfillmentStatus === 'preparing' ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200/50 dark:border-amber-900/50' :
                            tx.fulfillmentStatus === 'ready_for_pickup' ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-blue-200/50 dark:border-blue-900/50' :
                            tx.fulfillmentStatus === 'completed' ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border-emerald-200/50 dark:border-emerald-900/50' :
                            'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700'
                          }`}
                        >
                          <option value="pending" className="bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white">Pending</option>
                          <option value="preparing" className="bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white">Preparing</option>
                          <option value="ready_for_pickup" className="bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white">Ready for Pickup</option>
                          <option value="completed" className="bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white">Completed</option>
                        </select>
                      </div>
                    ) : (
                      <span className="text-xs text-neutral-400 dark:text-neutral-500 italic">Awaiting Payment</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>

      {/* Item Details Modal */}
      {selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-neutral-900/40 dark:bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200 border border-neutral-100 dark:border-neutral-800">
            <div className="relative h-48 bg-neutral-100 dark:bg-neutral-800">
              {selectedItem.imageUrl ? (
                <img src={selectedItem.imageUrl} alt={selectedItem.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Package className="w-12 h-12 text-neutral-300 dark:text-neutral-600" />
                </div>
              )}
              <button 
                onClick={() => setSelectedItem(null)}
                className="absolute top-4 right-4 p-2 bg-black/20 hover:bg-black/40 text-white rounded-full backdrop-blur-md transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6">
              <h3 className="text-xl font-serif font-medium text-neutral-900 dark:text-white mb-2">{selectedItem.name || 'Unknown Item'}</h3>
              <p className="text-lg font-medium text-neutral-900 dark:text-white mb-4">${((selectedItem.price || 0) / 100).toFixed(2)}</p>
              {selectedItem.description && (
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4 leading-relaxed">{selectedItem.description}</p>
              )}
              <div className="flex justify-end">
                <button
                  onClick={() => setSelectedItem(null)}
                  className="px-4 py-2 bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 text-neutral-900 dark:text-white rounded-xl text-sm font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
